/*
 * as.if Hoerer-Modus (SPA-Navigation)
 * =====================================
 * Solange as.if.radio laeuft, werden interne Links nicht neu geladen,
 * sondern nachgeladen: <main> wird getauscht, Kopf, Overlays, Hub und das
 * Audio-Element bleiben stehen. Fuer alle anderen Besucher passiert nichts,
 * jede Seite laedt wie bisher.
 *
 * Vertrag fuer neue Seiten und Skripte (bitte einhalten):
 *
 *  1. Alles, was beim Laden auf den Seiteninhalt zugreift, hoert zusaetzlich
 *     auf document 'asif:page' (detail: { url, template, title, initial })
 *     und ist idempotent: zweimal aufrufen darf nichts doppelt bauen.
 *     Aufraeumen vor dem Tausch: document 'asif:page:unload'.
 *     Sektionen bekommen ausserdem 'shopify:section:load' (wie im Editor).
 *
 *  2. Skripte, die im Body einer Seite stehen (inline oder src), werden
 *     nach dem Tausch ausgefuehrt. Externe Skripte nur einmal pro src.
 *     Inline-Skripte, die nicht noch einmal laufen duerfen, tragen
 *     data-asif-spa="once".
 *
 *  3. Seiten, die hart laden muessen (Formular-Apps, Captchas), setzen im
 *     Layout body[data-asif-spa="hard"] oder stehen in der Liste
 *     #asif-spa-config. Einzelne Links: a[data-asif-spa="hard"].
 *
 *  4. Tracking: nach jedem Tausch feuern wir
 *       Shopify.analytics.publish('asif_spa_page_view', {...})   (Custom Pixel)
 *       window.dataLayer.push({ event: 'asif_spa_page_view', ... })
 *       window.hj('stateChange', path)                          (Hotjar)
 *     App-Pixel von Shopify zaehlen nur echte Seitenladungen. Das ist der
 *     bewusste Preis des Hoerer-Modus, deshalb laeuft er nur bei Ton.
 *
 *  5. Im Theme-Editor (Shopify.designMode) ist der Modus aus.
 */
(() => {
  'use strict';

  if (window.AsIfSpa) return;
  if (window.Shopify?.designMode) return;
  if (!('fetch' in window) || !('DOMParser' in window) || !window.history?.pushState) return;

  const config = (() => {
    try { return JSON.parse(document.getElementById('asif-spa-config')?.textContent || '{}'); } catch { return {}; }
  })();
  const hardPaths = new Set((config.hardPaths || []).map((path) => path.replace(/\/+$/, '')));
  const hardPrefixes = ['/checkouts', '/checkout', '/account', '/admin', '/apps', '/tools', '/password', '/challenge', '/cdn', '/services'].concat(config.hardPrefixes || []);
  const TIMEOUT = 9000;
  const CACHE_TTL = 30000;
  const cache = new Map();
  const CACHE_MAX = 8;
  const pruneCache = () => {
    const now = Date.now();
    cache.forEach((entry, key) => {
      if (!entry || now - entry.at > CACHE_TTL) cache.delete(key);
    });
    while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
  };
  let controller = null;
  let navigating = false;
  let lastUrl = window.location.href;

  const state = () => {
    try { return window.AsIfRadio?.getState?.() || {}; } catch { return {}; }
  };
  // Billig halten: der Player bietet seit 2.2.0 einen Lesezugriff ohne Kopie an,
  // sonst reicht das Attribut am html-Element. getState() kopiert tief und wurde
  // hier bei jedem Hover ueber einen Link gerufen.
  const listening = () => {
    try {
      const radio = window.AsIfRadio;
      if (typeof radio?.isListening === 'function') {
        if (radio.isListening()) return true;
        const live = typeof radio.status === 'function' ? radio.status() : '';
        return ['playing', 'buffering', 'connecting', 'retrying'].includes(live);
      }
    } catch (error) {}
    const status = document.documentElement.dataset.asifRadioState || 'idle';
    return ['playing', 'buffering', 'connecting', 'retrying'].includes(status);
  };

  const isHardPath = (pathname) => {
    const clean = pathname.replace(/\/+$/, '') || '/';
    if (hardPaths.has(clean)) return true;
    return hardPrefixes.some((prefix) => clean === prefix || clean.startsWith(`${prefix}/`));
  };

  const eligibleLink = (event) => {
    if (event.defaultPrevented || event.button !== 0) return null;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    // composedPath statt closest: Links im Kopf liegen in Shadow DOM,
    // event.target ist dort nur der Host.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const link = path.find((node) => node instanceof HTMLAnchorElement && node.hasAttribute('href')) || event.target.closest?.('a[href]');
    if (!link) return null;
    if (link.target && link.target !== '_self') return null;
    // body[data-asif-spa="hard"] beschreibt die aktuelle Seite, nicht die Links darauf.
    if (link.hasAttribute('download') || link.dataset.asifSpa === 'hard' || link.closest('[data-asif-spa="hard"]:not(body)')) return null;
    if (link.getAttribute('href').startsWith('#')) return null;
    let url;
    try { url = new URL(link.href, window.location.href); } catch { return null; }
    if (url.origin !== window.location.origin) return null;
    if (!/^https?:$/.test(url.protocol)) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return null;
    if (isHardPath(url.pathname)) return null;
    if (/\.(pdf|zip|jpe?g|png|gif|webp|svg|mp3|mp4|ics|csv|xml|json|txt)$/i.test(url.pathname)) return null;
    return url;
  };

  const loadingStart = () => document.dispatchEvent(new CustomEvent('theme:loading:start'));
  const loadingEnd = () => document.dispatchEvent(new CustomEvent('theme:loading:end'));

  const announce = (text) => {
    const region = document.getElementById('status-announcement');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = text; }, 50);
  };

  const fetchPage = async (url, signal) => {
    const key = url.pathname + url.search;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL) return cached.html;
    const response = await fetch(url.href, { credentials: 'same-origin', signal, headers: { 'X-Requested-With': 'asif-spa' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const finalUrl = new URL(response.url || url.href, window.location.href);
    if (finalUrl.origin !== window.location.origin || isHardPath(finalUrl.pathname)) throw new Error('redirected-hard');
    const type = response.headers.get('content-type') || '';
    if (!type.includes('text/html')) throw new Error('not-html');
    const html = await response.text();
    cache.set(key, { html, at: Date.now(), url: finalUrl.href });
    pruneCache();
    return html;
  };

  const assetKey = (value) => {
    try { const url = new URL(value, window.location.href); return url.origin + url.pathname; } catch { return value; }
  };
  // Skripte im frisch eingesetzten <main> zaehlen nicht als geladen: sie kommen
  // aus dem DOMParser und laufen erst, wenn wir sie unten neu erzeugen.
  const loadedScripts = (exclude) => new Set([...document.scripts].filter((script) => script.src && !(exclude && exclude.contains(script))).map((script) => assetKey(script.src)));
  const loadedStyles = () => new Set([...document.querySelectorAll('link[rel="stylesheet"][href]')].map((link) => assetKey(link.href)));

  const loadStylesheets = (newDoc) => {
    const present = loadedStyles();
    const waits = [];
    newDoc.querySelectorAll('head link[rel="stylesheet"][href]').forEach((link) => {
      const key = assetKey(link.getAttribute('href'));
      if (present.has(key)) return;
      const clone = document.createElement('link');
      clone.rel = 'stylesheet';
      clone.href = link.getAttribute('href');
      if (link.media) clone.media = link.media;
      waits.push(new Promise((resolve) => {
        clone.addEventListener('load', resolve, { once: true });
        clone.addEventListener('error', resolve, { once: true });
        window.setTimeout(resolve, 2500);
      }));
      document.head.appendChild(clone);
      present.add(key);
    });
    return Promise.all(waits);
  };

  const runScript = (script) => new Promise((resolve) => {
    const clone = document.createElement('script');
    [...script.attributes].forEach((attribute) => clone.setAttribute(attribute.name, attribute.value));
    if (script.src) {
      clone.async = false;
      clone.addEventListener('load', resolve, { once: true });
      clone.addEventListener('error', resolve, { once: true });
      window.setTimeout(resolve, 4000);
    } else {
      clone.textContent = script.textContent;
    }
    script.replaceWith(clone);
    if (!script.src) resolve();
  });

  const isExecutable = (script) => {
    const type = (script.getAttribute('type') || '').trim().toLowerCase();
    return !type || type === 'text/javascript' || type === 'module' || type === 'application/javascript';
  };

  const runScripts = async (container, newDoc) => {
    const present = loadedScripts(container);
    // Skripte im getauschten Inhalt
    for (const script of [...container.querySelectorAll('script')]) {
      if (!isExecutable(script)) continue;
      if (script.src) {
        const key = assetKey(script.src);
        if (present.has(key)) { script.remove(); continue; }
        present.add(key);
        await runScript(script);
      } else {
        if (script.dataset.asifSpa === 'once') continue;
        await runScript(script);
      }
    }
    // Externe Skripte, die die Zielseite ausserhalb von <main> mitbringt
    // (Kopf und Body-Ende), aber hier noch fehlen: z. B. Skripte, die nur
    // auf der Radioseite geladen werden.
    for (const script of [...newDoc.querySelectorAll('script[src]')]) {
      if (!isExecutable(script) || script.closest('main')) continue;
      const key = assetKey(script.getAttribute('src'));
      if (present.has(key)) continue;
      present.add(key);
      const clone = document.createElement('script');
      clone.src = script.getAttribute('src');
      clone.async = false;
      if (script.type) clone.type = script.type;
      await new Promise((resolve) => {
        clone.addEventListener('load', resolve, { once: true });
        clone.addEventListener('error', resolve, { once: true });
        window.setTimeout(resolve, 4000);
        document.body.appendChild(clone);
      });
    }
  };

  const syncHead = (newDoc) => {
    document.title = newDoc.title;
    const swapMeta = (selector) => {
      const next = newDoc.head.querySelector(selector);
      const current = document.head.querySelector(selector);
      if (next && current) current.replaceWith(document.importNode(next, true));
      else if (next) document.head.appendChild(document.importNode(next, true));
      else if (current) current.remove();
    };
    ['meta[name="description"]', 'link[rel="canonical"]', 'meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:url"]', 'meta[property="og:image"]', 'meta[name="robots"]', 'link[rel="manifest"]', 'meta[name="application-name"]', 'meta[name="apple-mobile-web-app-title"]', 'meta[name="apple-mobile-web-app-capable"]', 'meta[name="apple-mobile-web-app-status-bar-style"]'].forEach(swapMeta);
    const themeColor = newDoc.head.querySelector('meta[data-asif-theme-color]');
    const currentThemeColor = document.head.querySelector('meta[data-asif-theme-color]');
    if (themeColor && currentThemeColor) currentThemeColor.content = themeColor.content;
    document.head.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
    newDoc.head.querySelectorAll('script[type="application/ld+json"]').forEach((node) => document.head.appendChild(document.importNode(node, true)));
  };

  const syncBody = (newDoc) => {
    const keep = ['asif-message-hub-open'].filter((name) => document.body.classList.contains(name));
    document.body.className = newDoc.body.className;
    keep.forEach((name) => document.body.classList.add(name));
    ['data-asif-template', 'data-asif-spa', 'data-pdp'].forEach((name) => {
      const value = newDoc.body.getAttribute(name);
      if (value === null) document.body.removeAttribute(name);
      else document.body.setAttribute(name, value);
    });
  };

  // Seitenbezogene Overlays ausserhalb von <main>, die die alte Seite
  // hinterlassen hat (Alterspruefung), verschwinden mit ihr.
  const syncExtras = () => {
    document.querySelectorAll('[data-asif-agegate]').forEach((node) => node.remove());
    document.documentElement.classList.remove('asif-agegate-active');
  };

  const dispatchSectionEvents = (container) => {
    container.querySelectorAll('.shopify-section').forEach((section) => {
      const sectionId = section.id.replace(/^shopify-section-/, '');
      section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } }));
    });
  };

  const scrollTo = (url, pop, savedY) => {
    if (pop && typeof savedY === 'number') { window.scrollTo(0, savedY); return; }
    if (url.hash) {
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (target) { target.scrollIntoView({ block: 'start' }); return; }
    }
    window.scrollTo(0, 0);
  };

  const focusMain = () => {
    const main = document.getElementById('main');
    if (!main) return;
    const heading = main.querySelector('h1');
    const target = heading || main;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  const track = (url, template) => {
    const detail = { url: url.href, path: url.pathname + url.search, title: document.title, template, mode: 'listener' };
    try { window.Shopify?.analytics?.publish?.('asif_spa_page_view', detail); } catch {}
    try { (window.dataLayer = window.dataLayer || []).push({ event: 'asif_spa_page_view', ...detail }); } catch {}
    try { if (typeof window.hj === 'function') window.hj('stateChange', detail.path); } catch {}
  };

  const navigate = async (url, { pop = false, savedY } = {}) => {
    if (navigating) controller?.abort();
    navigating = true;
    // 23.09.2026: eigener Controller je Wechsel. Vorher brach der Timeout eines
    // alten Wechsels den neuen ab, und ein ueberholter Wechsel lud hart nach.
    const own = new AbortController();
    controller = own;
    const signal = own.signal;
    const timeout = window.setTimeout(() => own.abort(), TIMEOUT);
    document.dispatchEvent(new CustomEvent('asif:page:start', { detail: { url: url.href } }));
    loadingStart();
    try {
      const html = await fetchPage(url, signal);
      if (signal.aborted) return;
      const newDoc = new DOMParser().parseFromString(html, 'text/html');
      const newMain = newDoc.getElementById('main');
      const main = document.getElementById('main');
      if (!newMain || !main) throw new Error('no-main');
      if (newDoc.body.dataset.asifSpa === 'hard') throw new Error('target-hard');
      // Seiten mit Alterspruefung (Overlay ausserhalb von <main>) laden hart.
      if (newDoc.querySelector('[data-asif-agegate]')) throw new Error('target-agegate');

      document.dispatchEvent(new CustomEvent('asif:page:unload', { detail: { url: window.location.href } }));
      document.querySelectorAll('main .shopify-section').forEach((section) => {
        section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId: section.id.replace(/^shopify-section-/, '') } }));
      });

      await loadStylesheets(newDoc);
      if (signal.aborted) return;

      if (!pop) {
        history.replaceState({ asifSpa: true, y: window.scrollY }, '', window.location.href);
        history.pushState({ asifSpa: true, y: 0 }, '', url.href);
      }
      syncHead(newDoc);
      syncBody(newDoc);
      syncExtras();
      const imported = document.importNode(newMain, true);
      main.replaceWith(imported);
      document.querySelectorAll('.hub__panel[open]').forEach((dialog) => dialog.close());

      const template = document.body.dataset.asifTemplate || '';
      await runScripts(imported, newDoc);
      dispatchSectionEvents(imported);
      document.dispatchEvent(new CustomEvent('asif:page', { detail: { url: url.href, template, title: document.title, initial: false } }));
      scrollTo(url, pop, savedY);
      focusMain();
      announce(`Seite geladen: ${document.title}`);
      track(url, template);
      lastUrl = window.location.href;
    } catch (error) {
      if (controller !== own || (signal.aborted && !navigating)) return;
      // Jeder Fehler: echte Navigation, nichts halb fertig lassen.
      try { console.warn('[asif-spa] hart geladen:', error?.message || error); } catch {}
      window.location.assign(url.href);
      return;
    } finally {
      window.clearTimeout(timeout);
      if (controller === own) {
        navigating = false;
        loadingEnd();
      }
    }
  };

  document.addEventListener('click', (event) => {
    if (!listening()) return;
    const url = eligibleLink(event);
    if (!url) return;
    event.preventDefault();
    navigate(url);
  });

  // Vorladen bei Absicht: erst bei Ton, nur GET-Seiten, kleiner Cache.
  let prefetchTimer = 0;
  const prefetch = (event) => {
    if (!listening()) return;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const link = path.find((node) => node instanceof HTMLAnchorElement && node.hasAttribute('href')) || event.target.closest?.('a[href]');
    if (!link) return;
    let url;
    try { url = new URL(link.href, window.location.href); } catch { return; }
    if (url.origin !== window.location.origin || isHardPath(url.pathname) || link.dataset.asifSpa === 'hard') return;
    const key = url.pathname + url.search;
    if (cache.has(key) || key === window.location.pathname + window.location.search) return;
    window.clearTimeout(prefetchTimer);
    prefetchTimer = window.setTimeout(() => { fetchPage(url).catch(() => {}); }, 90);
  };
  // pointerenter mit capture feuert fuer jedes Element unter dem Zeiger. Ein
  // Vorfilter auf Links haelt die Arbeit vom Haupt-Thread fern.
  const maybePrefetch = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('a[href]')) return;
    prefetch(event);
  };
  document.addEventListener('pointerenter', maybePrefetch, true);
  document.addEventListener('touchstart', maybePrefetch, { passive: true, capture: true });

  window.addEventListener('popstate', (event) => {
    if (!event.state?.asifSpa) return;
    if (!listening()) { window.location.reload(); return; }
    const url = new URL(window.location.href);
    if (url.href === lastUrl) return;
    navigate(url, { pop: true, savedY: event.state.y });
  });

  if ('scrollRestoration' in history) history.scrollRestoration = 'auto';

  window.AsIfSpa = Object.freeze({
    version: '1.0.0',
    navigate: (href) => { try { navigate(new URL(href, window.location.href)); } catch {} },
    listening,
    isHardPath
  });

  // Erster Aufruf: gleiche Kontraktnachricht wie nach einem Tausch, damit
  // Skripte einen einzigen Einstieg brauchen.
  const fireInitial = () => document.dispatchEvent(new CustomEvent('asif:page', { detail: { url: window.location.href, template: document.body.dataset.asifTemplate || '', title: document.title, initial: true } }));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fireInitial, { once: true });
  else fireInitial();
})();
